/**
 * Обязательства хозяйства по одной работе — из `GET /farm-obligations/{id}`.
 *
 * Это место, где ярлык прототипа перестаёт быть полем файла и становится
 * выводом. В `obligations-<цикл>.json` лежало одно поле `state` с пятью
 * значениями; канон хранит три независимые оси (состояние «отчёт × проверка»,
 * положение среза относительно окна, срок), и ярлык считается из них чистой
 * функцией `obligationMark` (решение 4 плана `astra-on-canon`). Хранить ярлык
 * запрещено: «выполнено» обязано оставаться следствием подтверждённого отчёта,
 * а не свойством строки, которое можно записать мимо проверки.
 *
 * «Расхождение» (`conflict` / `needs_clarification`) не шестой ярлык, а
 * отдельный признак: обязательство отчитано, но проверка с отчётом не сошлась.
 * Прототип такого поля не знал, поэтому оно едет рядом со строкой — интерфейс
 * обязан сказать это ВМЕСТЕ с ярлыком, а не вместо него.
 *
 * Чего здесь нет: набора обязательств на ЦИКЛ целиком. Контурные строки канон
 * отдаёт по одному обязательству хозяйства, и собрать из них цикл значило бы
 * сделать по запросу на каждое хозяйство каждой кампании — ровно тот обход
 * корпуса в браузере, ради отказа от которого сервер считает сводку. Пока у
 * канона нет постраничного списка контурных строк кампании, экраны цикла
 * показывают «нет данных» там, где раньше стояли фикстурные числа.
 */

import { type QueryParams, type Schemas, api } from '../../../api/client';
import {
  deadlinePosition,
  obligationMark,
  windowPosition,
} from '../../model/obligationLabel';
import type { Obligation } from '../../data/types';
import { type DatasetAdapter, ha, once, paramsKey } from './adapter';
import { localIsoDate } from '../../../lib/localDate';
import { serverToday } from '../../../api/serverToday';

type FarmObligation = Schemas['FarmObligationOut'];
type ContourRow = Schemas['ObligationContourOut'];

const PREFIX = 'farm-obligation-';
const SUFFIX = '.json';

/** Строка прототипа плюс признак, которого прототип не знал. */
export type ObligationRow = Obligation & {
  /** Отчёт подан, но проверка с ним не сошлась. */
  discrepancy: boolean;
};

/** Дата независимой проверки — самая поздняя из заявленных. */
export function verifiedOn(row: ContourRow): string | null {
  const dates = row.verifications.map((claim) => claim.occurred_on).sort();
  return dates[dates.length - 1] ?? null;
}

/**
 * Контурная строка канона → обязательство прототипа.
 *
 * Окно берётся у строки, а если его там нет — у обязательства хозяйства: окно
 * приходит от работы шаблона и одинаково для всех её контуров. Срок — конец
 * окна: отдельного поля «срок» у контурной строки нет, и выдумывать его здесь
 * нельзя. Окна нет вовсе — срока нет, и вопрос о просрочке не задан.
 */
export function toObligation(
  row: ContourRow,
  item: FarmObligation,
  asOf: string,
): ObligationRow {
  const from = row.window_from ?? item.window_from;
  const to = row.window_to ?? item.window_to;
  const mark = obligationMark({
    state: row.state,
    // Без окна положение среза неопределимо; «до открытия» — единственный
    // ответ, не объявляющий отчёт просроченным.
    window: from && to ? windowPosition({ from, to }, asOf) : 'before',
    deadline: deadlinePosition(to, asOf),
  });

  return {
    id: String(row.obligation_id),
    work_id: String(item.campaign_id),
    farm_id: String(item.actor.id),
    contour_id: String(row.contour_id),
    ...(row.contour_part_id != null ? { contour_part_id: String(row.contour_part_id) } : {}),
    area_ha: ha(row.area_microha) as number,
    state: mark.label,
    discrepancy: mark.discrepancy,
    reported_on: row.reported_on,
    verified_on: verifiedOn(row),
    // «С опозданием» — отчёт ПОСЛЕ конца окна. Без даты отчёта или без окна
    // утверждение не делается: незнание не равно «вовремя».
    late: Boolean(row.reported_on && to && row.reported_on > to),
    reported_by: row.reported_by,
    // «От чьего имени» заполняется только когда запись действительно внесена
    // не хозяйством: иначе поле читалось бы как чужой ввод.
    on_behalf: row.entered_on_behalf ? (item.actor.name ?? String(item.actor.id)) : null,
    ...(item.epistemic_status === 'synthetic_demo' ? { evidence: 'demo' as const } : {}),
  };
}

export const obligationsAdapter: DatasetAdapter = {
  domain: 'obligations',

  match(name) {
    return name.startsWith(PREFIX) && name.endsWith(SUFFIX);
  },

  async load(name, params: QueryParams) {
    const id = Number(name.slice(PREFIX.length, name.length - SUFFIX.length));
    const detail = await once(`farm-obligation|${id}|${paramsKey(params)}`, () =>
      api.farmObligation(id, params),
    );
    // Дата среза — та, по которой сервер считал ответ. Взять «сегодня» значило
    // бы посчитать ярлыки на одну дату, а числа рядом — на другую. Без среза
    // в контексте это «сегодня» сервера, а не часы вкладки.
    const asOf = detail.context.as_of ?? (await serverToday()) ?? localIsoDate();
    return detail.contours.map((row) => toObligation(row, detail.item, asOf));
  },
};
